import { Colli } from '@frontend/colli/types';
import { SlotRendering } from '@frontend/common-types';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageTriggerName, PackageType } from '@frontend/package/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import React, { useEffect, useMemo, useState } from 'react';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../components/slot-layout-cell';
import { useScreen } from '../screens/useScreen';
import { getSeededPackagesFromWizardData, packageSlotIds, resolveOpenablePackagesForCollis } from '../utils/colli-open-slot-context';
import { useWizardPackage } from './useWizardPackage';
import { useWizardTransaction } from './useWizardTransaction';

export interface ColliConfirmOpenSlotsParallelViewProps {
    accountId?: string;
    spotId?: string;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlotIds: string[];
    collis: Colli[];
    packages: Package[];
    resolveError: boolean;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
}

interface UseColliConfirmOpenSlotsParallelArgs {
    screenType: ScreenType;
    packageType: PackageType.DROP_OFF | PackageType.PICK_UP;
}

export function useColliConfirmOpenSlotsParallel(args: UseColliConfirmOpenSlotsParallelArgs): ColliConfirmOpenSlotsParallelViewProps {
    const { navigationState, store, updateWizardData, onBack, onSubmit: onContinue } = useScreen();
    const { transaction } = useWizardTransaction({ wizardId: store!.wizard.id, store });
    const { localUpdatePackage } = useWizardPackage({ wizardId: store!.wizard.id, store });

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [packages, setPackages] = useState<Package[]>([]);
    const [resolveError, setResolveError] = useState(false);
    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const collis = useMemo(() => (store?.data.collis as Colli[] | undefined) ?? [], [store?.data.collis]);
    const selectedSlotIds = useMemo(() => packageSlotIds(packages), [packages]);

    useEffect(() => {
        const accountId = navigationState.iot?.account_id || transaction?.account_id;
        const spotId = navigationState.iot?.spot_id || transaction?.spot_id;
        if (!accountId || !spotId) return;

        ModuleClient.fetchModules({ spot_id: spotId }).then((result) => {
            const sorted = result.results.sort((a, b) => (a.index || '').localeCompare(b.index || '', undefined, { numeric: true, sensitivity: 'base' }));
            setModules(sorted);
        });
        SlotClient.fetchSlots({ spot_id: spotId, size: '1000', index: '0' }).then((result) => setAllSlots(result.results));
    }, []);

    useEffect(() => {
        const accountId = navigationState.iot?.account_id || transaction?.account_id || (store?.data.package as Package | undefined)?.account_id;
        const transactionId = transaction?.id || (store?.data.package as Package | undefined)?.transaction_id;
        if (!accountId || !transactionId) return;

        let cancelled = false;
        setLoading(true);
        setResolveError(false);

        resolveOpenablePackagesForCollis({
            accountId,
            transactionId,
            spotId: navigationState.spot?.id ?? navigationState.iot?.spot_id,
            collis,
            type: args.packageType,
            seededPackages: getSeededPackagesFromWizardData(store?.data as Record<string, unknown>)
        })
            .then((resolved) => {
                if (cancelled) return;
                setPackages(resolved);
                if (resolved.length === 0) {
                    setResolveError(true);
                    return;
                }
                updateWizardData({ packages: resolved, package: resolved[0] });
                localUpdatePackage(resolved[0]);
            })
            .catch(() => {
                if (!cancelled) setResolveError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [transaction?.id, args.packageType, collis, store?.data.package?.id]);

    useEffect(() => {
        if (!modules || packages.length === 0) return;
        const moduleId = packages[0]?.module_id;
        if (!moduleId) return;
        const idx = modules.findIndex((m) => m.id === moduleId);
        if (idx >= 0) setCurrentModuleIndex(idx);
    }, [modules, packages]);

    const currentModule = modules?.[currentModuleIndex] ?? null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) setCurrentModuleIndex(index);
        }
        setSelectModuleModalOpen(false);
    };

    const resolveNextStep = (count: number): Step | undefined => {
        const submitStep = store?.steps.find((step) => step.screen === args.screenType && step.trigger === ScreenTrigger.SUBMIT);
        if (count > 1) {
            const multiStep = store?.steps.find((step) => step.screen === args.screenType && step.next_screen === ScreenType.PROCESS_OPEN_CLOSE_SLOTS);
            if (multiStep) return multiStep;
            if (submitStep) return { ...submitStep, next_screen: ScreenType.PROCESS_OPEN_CLOSE_SLOTS };
        }
        return submitStep;
    };

    const onSubmit = async () => {
        if (!store || packages.length === 0 || selectedSlotIds.length === 0) return;

        updateWizardData({ packages, package: packages[0] });

        await Promise.all(
            packages.map((pkg) =>
                PackageWorkflowClient.triggerPackageState(pkg.account_id, pkg.transaction_id, pkg.id, {
                    trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                })
            )
        ).finally(() => {
            onContinue(resolveNextStep(packages.length));
        });
    };

    const slotComponent = (slotRendering: SlotRendering): React.ReactNode => {
        const slot = allSlots?.find((s) => s.id === slotRendering.id) ?? null;
        const isHighlighted = !!slot?.id && selectedSlotIds.includes(slot.id);

        return (
            <SlotLayoutCell
                slotNumber={slotLayoutDisplayNumber(slot)}
                isSelected={isHighlighted}
                isDisabled={slot?.type === SlotType.TERMINAL}
                hasContent={false}
                onSelect={isHighlighted ? onSubmit : undefined}
            />
        );
    };

    return {
        accountId: navigationState.iot?.account_id || transaction?.account_id || undefined,
        spotId: navigationState.iot?.spot_id || transaction?.spot_id || undefined,
        currentModule,
        modules,
        selectedSlotIds,
        collis,
        packages,
        resolveError: resolveError || (!loading && packages.length === 0),
        setCurrentModule,
        slotComponent,
        submitDisabled: loading || packages.length === 0 || selectedSlotIds.length === 0,
        onSubmit,
        onBack,
        selectModuleModalOpen,
        setSelectModuleModalOpen
    };
}
